import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

const CACHE_KEY = 'pfm_homepage_cache';
const CACHE_TIMESTAMP_KEY = 'pfm_homepage_cache_timestamp';
// Bump this version whenever the Firestore data shape changes (e.g. plain→multilingual)
const CACHE_VERSION = 'v2_multilingual';
const REVALIDATE_TTL_MS = 60 * 1000;
const memoryCache = new Map();
const inflightRequests = new Map();
let legacyCachePurged = false;

const getCacheKey = (collection, documentId) =>
    `${CACHE_KEY}_${CACHE_VERSION}_${collection}_${documentId}`;

const getTimestampKey = (collection, documentId) =>
    `${CACHE_TIMESTAMP_KEY}_${CACHE_VERSION}_${collection}_${documentId}`;

const getDocKey = (collection, documentId) => `${collection}/${documentId}`;

const purgeLegacyCache = () => {
    if (legacyCachePurged) return;

    try {
        Object.keys(localStorage).forEach((key) => {
            if (
                key.startsWith('pfm_') &&
                !key.includes(CACHE_VERSION) &&
                (key.includes('_cache') || key.includes('_timestamp'))
            ) {
                localStorage.removeItem(key);
            }
        });
    } catch (_) {
        // no-op
    }

    legacyCachePurged = true;
};

const readLocalCache = (collection, documentId) => {
    const cacheKey = getCacheKey(collection, documentId);
    const timestampKey = getTimestampKey(collection, documentId);

    try {
        const cached = localStorage.getItem(cacheKey);
        const timestamp = localStorage.getItem(timestampKey);

        if (!cached) return null;

        const cachedData = JSON.parse(cached);
        const heroTag = cachedData?.hero?.tag;

        if (typeof heroTag === 'string') {
            localStorage.removeItem(cacheKey);
            localStorage.removeItem(timestampKey);
            return null;
        }

        return {
            data: cachedData,
            timestamp: timestamp ? parseInt(timestamp, 10) : 0,
        };
    } catch (_) {
        return null;
    }
};

const writeCache = (collection, documentId, data) => {
    const timestamp = Date.now();
    const docKey = getDocKey(collection, documentId);
    const cacheEntry = { data, timestamp };

    memoryCache.set(docKey, cacheEntry);

    try {
        localStorage.setItem(getCacheKey(collection, documentId), JSON.stringify(data));
        localStorage.setItem(getTimestampKey(collection, documentId), timestamp.toString());
    } catch (_) {
        // no-op
    }

    return cacheEntry;
};

const getCachedEntry = (collection, documentId) => {
    const docKey = getDocKey(collection, documentId);
    if (memoryCache.has(docKey)) {
        return memoryCache.get(docKey);
    }

    const localEntry = readLocalCache(collection, documentId);
    if (localEntry) {
        memoryCache.set(docKey, localEntry);
        return localEntry;
    }

    return null;
};

const fetchFreshDoc = async (collection, documentId) => {
    const docKey = getDocKey(collection, documentId);

    if (inflightRequests.has(docKey)) {
        return inflightRequests.get(docKey);
    }

    const request = getDoc(doc(db, collection, documentId))
        .then((docSnap) => {
            if (!docSnap.exists()) {
                throw new Error('Document not found');
            }

            const freshData = docSnap.data();
            return writeCache(collection, documentId, freshData);
        })
        .finally(() => {
            inflightRequests.delete(docKey);
        });

    inflightRequests.set(docKey, request);
    return request;
};

/**
 * Custom hook to fetch a Firestore document with caching
 * Uses stale-while-revalidate pattern:
 * 1. Return cached data immediately (no loading screen)
 * 2. Fetch fresh data in background
 * 3. Update with fresh data when available
 * 
 * @param {string} collection - Collection name
 * @param {string} documentId - Document ID
 * @returns {Object} { data, loading, error }
 */
export const useFirestoreDoc = (collection, documentId) => {
    const initialEntry = getCachedEntry(collection, documentId);
    const [data, setData] = useState(initialEntry?.data || null);
    const [loading, setLoading] = useState(!initialEntry);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;

        purgeLegacyCache();

        const cachedEntry = getCachedEntry(collection, documentId);
        if (cachedEntry) {
            setData(cachedEntry.data);
            setLoading(false);
        } else {
            setLoading(true);
        }

        const shouldRevalidate = !cachedEntry ||
            (Date.now() - (cachedEntry.timestamp || 0)) > REVALIDATE_TTL_MS;

        if (!shouldRevalidate) {
            return () => {
                cancelled = true;
            };
        }

        fetchFreshDoc(collection, documentId)
            .then((entry) => {
                if (cancelled) return;
                setData(entry.data);
                setError(null);
            })
            .catch((err) => {
                if (cancelled) return;
                setError(err.message);
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [collection, documentId]);

    return { data, loading, error };
};

/**
 * Clear cache for a specific document
 * Useful after updating content in admin panel
 */
export const clearDocCache = (collection, documentId) => {
    const cacheKey = getCacheKey(collection, documentId);
    const timestampKey = getTimestampKey(collection, documentId);

    try {
        memoryCache.delete(getDocKey(collection, documentId));
        localStorage.removeItem(cacheKey);
        localStorage.removeItem(timestampKey);
    } catch (_) {
        // no-op
    }
};
